import { reactive, toRefs, nextTick } from 'vue';
import { isDefinedAndNotEmpty } from '../isDefinedAndNotEmpty.js';

const supportedLanguages = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'es', name: 'Español', dir: 'ltr' },
  { code: 'es-AR', name: 'Español (Argentina)', dir: 'ltr' },
  { code: 'he', name: 'עברית', dir: 'rtl' },
  { code: 'ru', name: 'Русский', dir: 'ltr' },
  { code: 'it', name: 'Italiano', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' }
];


let translationsReadyResolve;
export const translationsReady = new Promise(resolve => { translationsReadyResolve = resolve; });
function getLangFromUrl() {
  try {
    const params = new URL(window.location.href).searchParams;
    const langParam = params.get('lang');
    if (typeof langParam === 'string' && langParam !== undefined && langParam !== null && langParam !== '') {
      return langParam;
    }
    return null;
  } catch (e) {
    return null;
  }
}

function isSupportedLanguage(lang) {
  return supportedLanguages.find(l => l.code === lang) !== undefined;
}

const initialUrlLang = getLangFromUrl();
const initialCookieLang = getLanguageCookie();
const initialLang = isSupportedLanguage(initialUrlLang) === true ? initialUrlLang : (isDefinedAndNotEmpty(initialCookieLang) != false ? initialCookieLang : 'en');
if (isSupportedLanguage(initialUrlLang) === true) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000));
  const sameSiteLax = (process.env.environment === 'production') ? '; SameSite=Lax' : '';
  document.cookie = `selectedLanguage=${initialUrlLang};expires=${expires.toUTCString()};path=/${sameSiteLax}`;
}

const state = reactive({
  currentLanguage: initialLang,
  translations: {},
  availableLanguages: supportedLanguages
});

async function loadTranslations(lang) {
  try {
    const module = await import(`./locales/${lang}.json`);
    
    for (const key in state.translations) {
      if (state.translations.hasOwnProperty(key) !== true) {
        delete state.translations[key];
      }
    }
    Object.assign(state.translations, module.default);
    console.log(`Translations loaded for ${lang}:`, state.translations);

    
    translationsReadyResolve();

    const languageDirection = supportedLanguages.find(l => l.code === lang)?.dir || 'ltr';
    document.documentElement.dir = languageDirection;
    document.documentElement.lang = lang;

  } catch (error) {
    console.error(`Failed to load translations for ${lang}:`, error);
    if (lang !== 'en') {
      await loadTranslations('en');
    }
  }
}

function setLanguageCookie(lang) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000));
  const sameSiteLax = (process.env.environment === 'production') ? '; SameSite=Lax' : '';
  document.cookie = `selectedLanguage=${lang};expires=${expires.toUTCString()};path=/${sameSiteLax}`;
}

function getLanguageCookie() {
  const name = 'selectedLanguage=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

function getTranslationByKey(key, translations) {
  return key.split('.').reduce((obj, k) => (obj && obj[k] !== undefined && obj[k] !== null) ? obj[k] : null, translations);
}

export function applyTranslationsToDOM(rootElement = document.body) {
  if (rootElement === null || typeof rootElement.querySelectorAll !== 'function') {
    console.warn('applyTranslationsToDOM: rootElement is not a valid DOM element. Defaulting to document.body. Provided rootElement:', rootElement);
    rootElement = document.body;
    if (rootElement === null || typeof rootElement.querySelectorAll !== 'function') {
      console.error('applyTranslationsToDOM: document.body is also not a valid DOM element. Cannot apply translations.');
      return;
    }
  }

  if (Object.keys(state.translations).length === 0) {
    console.warn("Translations not loaded yet, skipping DOM update.");
    return;
  }
  const elements = rootElement.querySelectorAll('[data-translate]');
  for (const el of elements) {
    const key = el.getAttribute('data-translate');
    if (key !== null && key !== ''){
        const translation = getTranslationByKey(key, state.translations);
        if (isDefinedAndNotEmpty(translation) != false) {
            el.textContent = translation;
        } else {
            console.warn(`DOM translation not found for key: ${key} in language: ${state.currentLanguage}`);
            
              
        }
    }
  }
}

export function useI18n() {
  const setCurrentLanguage = async (lang) => {
    if (supportedLanguages.find(l => l.code === lang)) {
      state.currentLanguage = lang;
      setLanguageCookie(lang);
      await loadTranslations(lang);
    } else {
      console.warn(`Language ${lang} is not supported.`);
    }
  };

  const t = (key, arg2, arg3) => {
    let translation = getTranslationByKey(key, state.translations);
    let replacements = {};
    let fallback = null;

    if (typeof arg2 === 'string') {
        fallback = arg2;
        if (typeof arg3 === 'object' && arg3 !== null) {
            replacements = arg3;
        }
    } else if (typeof arg2 === 'object' && arg2 !== null) {
        replacements = arg2;
        if (typeof arg3 === 'string') {
            fallback = arg3;
        }
    }
    
    if (translation === undefined || translation === null) {
      console.warn(`t() func: Translation not found for key: ${key} in language: ${state.currentLanguage}`);
      translation = fallback !== null ? fallback : key;
    }

    if (typeof translation === 'string') {
      for (const [placeholder, value] of Object.entries(replacements)) {
        translation = translation.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value);
      }
    }
    return translation;
  };
 
  return {
    ...toRefs(state),
    setCurrentLanguage,
    t
  };
}

loadTranslations(initialLang);